'use client';

import { Avatar, Box, Button, Flex, Popover, Portal, Separator, Text } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { RxGear } from 'react-icons/rx';

import { ChangeEvent, Menu, MenuContent, MenuGroup, MenuItem } from '@components/common/Menu';
import { Tooltip } from '@components/ui/tooltip';
import LoginModal from '@features/login/LoginModal';
import CreateOrgMenuItem, {
  NEW_BUSINESS_MENU_ITEM_VALUE,
} from '@features/navigation/CreateOrgMenuItem';
import { NAVIGATION_PANEL_MIN_WIDTH_PX } from 'config/layout/navigationPanel';
import { openOnboarding } from 'helpers/appRoutes';
import { toPxString } from 'helpers/styles';
import useControlledPopover from 'hooks/useControlledPopover';
import useIsTruncated from 'hooks/useIsTruncated';
import { useSettingsNavigationEntities } from 'hooks/useNavigationEntities';
import { ModalType } from 'stores/pageStore';
import { useStores } from 'stores/rootStore';
import PeaceSign from 'vectors/PeaceSign';
import Plus from 'vectors/Plus';
import TrendArrow from 'vectors/TrendArrow';

const LOGOUT_MENU_ITEM_VALUE = 'logout';
const SETTINGS_MENU_ITEM_VALUE = 'settings';
const VIEW_MY_BUSINESSES_MENU_ITEM_VALUE = 'view-my-businesses';

interface Props {
  showOrgList?: boolean;
  shouldRedirectToLogin?: boolean;
  enableCreateBusiness?: boolean;
  hideSettings?: boolean;
}
const UserAvatarMenu = ({
  hideSettings = false,
  showOrgList = false,
  shouldRedirectToLogin = true,
  enableCreateBusiness,
}: Props) => {
  const { open, onToggle, onClose, triggerRef, contentRef } = useControlledPopover();
  const { userStore, pageStore, orgStore } = useStores();
  const user = userStore.user;
  const selectedOrg = orgStore.selectedOrg;
  const orgs = orgStore.orgs;
  const hasBusinesses = orgStore.orgs.length > 0;
  const { ref, isTruncated } = useIsTruncated();
  const { all } = useSettingsNavigationEntities();
  const router = useRouter();

  const openSettings = useCallback(() => {
    if (pageStore.selectedSettingPath != null) {
      router.replace(pageStore.selectedSettingPath);
      return;
    }
    const defaultSetting = all[0];
    if (defaultSetting != null && defaultSetting.absolutePath != null) {
      router.replace(defaultSetting.absolutePath);
    }
  }, [all, pageStore, router]);

  const onSelect = useCallback(
    (e: ChangeEvent) => {
      e.preventDefault();
      if (e.value === NEW_BUSINESS_MENU_ITEM_VALUE) {
        // early exit we don't want to close the popover
        return;
      }
      if (e.value === LOGOUT_MENU_ITEM_VALUE) {
        userStore.logOut({ shouldRedirectToLogin });
      } else if (e.value === SETTINGS_MENU_ITEM_VALUE) {
        openSettings();
      } else if (e.value === VIEW_MY_BUSINESSES_MENU_ITEM_VALUE) {
        openOnboarding();
        return;
      } else {
        const org = orgs.find((o) => o.id === e.value);
        if (org != null && org.id !== selectedOrg?.id) {
          window.location.href = `/${org.slug}`;
        }
      }
      onClose();
    },
    [onClose, userStore, orgs, selectedOrg, shouldRedirectToLogin, openSettings],
  );

  const onClickLogin = useCallback(() => {
    pageStore.openModal(ModalType.Login);
  }, [pageStore]);

  if (user == null) {
    return (
      <>
        <Button size="2xs" variant="solid" onClick={onClickLogin}>
          Sign in
        </Button>
        <LoginModal />
      </>
    );
  }

  const emailText = (
    <Box ref={ref} width={toPxString(NAVIGATION_PANEL_MIN_WIDTH_PX)} mb={2}>
      <Text color="text.secondary" fontWeight="medium" userSelect="none" truncate={isTruncated}>
        {user.email}
      </Text>
    </Box>
  );
  const textWithTooltip = isTruncated ? (
    <Tooltip content={user.email}>{emailText}</Tooltip>
  ) : (
    emailText
  );

  return (
    <Popover.Root open={open} onOpenChange={onToggle} lazyMount>
      <Popover.Trigger asChild>
        <Flex
          ref={triggerRef}
          flexDirection="row"
          alignItems="center"
          borderRadius="full"
          cursor="pointer"
        >
          <Avatar.Root size="2xs">
            <Avatar.Fallback name={user.name} />
            <Avatar.Image src={user.profileImage} />
          </Avatar.Root>
        </Flex>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            borderRadius={8}
            ref={contentRef}
            w="full"
            bgColor="surface.background"
            minHeight="fit-content"
            minW="xs"
            maxHeight="none" // Add this to remove height constraint
            overflow="visible" // Add this to ensure content isn't cut off
            p={2}
          >
            {textWithTooltip}
            <Menu value={selectedOrg?.id} showSelected onChange={onSelect} focus fontSize="xs">
              {showOrgList ? (
                <MenuGroup title="Businesses">
                  {orgs.map((org) => (
                    <MenuItem
                      key={org.id}
                      value={org.id}
                      pr="2px"
                      justifyContent="space-between"
                      alignItems="center"
                      textOverflow="ellipsis"
                    >
                      <MenuContent
                        value={
                          <Flex alignItems="center" gap={2} py={1}>
                            <Avatar.Root size="xs">
                              <Avatar.Fallback name={org.name} />
                            </Avatar.Root>
                            <Text textAlign="left">{org.name}</Text>
                          </Flex>
                        }
                      />
                    </MenuItem>
                  ))}
                </MenuGroup>
              ) : (
                <MenuItem showSelected={false} value={VIEW_MY_BUSINESSES_MENU_ITEM_VALUE}>
                  <MenuContent
                    value={hasBusinesses ? 'View my businesses' : 'Setup a business'}
                    icon={
                      hasBusinesses ? (
                        <TrendArrow boxSize={3} direction="up" />
                      ) : (
                        <Plus boxSize={3} />
                      )
                    }
                  />
                </MenuItem>
              )}
              <Separator borderColor="border.light" my={1} />
              {enableCreateBusiness && <CreateOrgMenuItem />}
              {!hideSettings && (
                <MenuItem showSelected={false} value={SETTINGS_MENU_ITEM_VALUE}>
                  <MenuContent value="Settings" icon={<RxGear />} />
                </MenuItem>
              )}
              <MenuItem showSelected={false} value={LOGOUT_MENU_ITEM_VALUE}>
                <MenuContent value="Logout" icon={<PeaceSign />} />
              </MenuItem>
            </Menu>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

export default observer(UserAvatarMenu);
